export const site = {
  name: 'MPANGE',
  initials: 'MP',
  tagline: 'Communication infrastructure for African businesses',
  email: 'info@mpanges.com',
  location: 'Dar es Salaam, Tanzania',
};

export const nav = [
  { href: '/#top', label: 'Home' },
  { href: '/#platform', label: 'About' },
  { href: '/#channels', label: 'Channels' },
  { href: '/#products', label: 'Products' },
  { href: '/developers', label: 'Developers' },
  { href: '/#contact', label: 'Contact' },
];

export const hero = {
  chip: 'MPANGE Technology Solution',
  typedNameLines: ['Communication', 'Infrastructure'],
  headlineKicker: 'for African businesses',
  rotating: ['SMS', 'USSD', 'Voice', 'IVR', 'WhatsApp', 'APIs'],
  body: 'MPANGE connects businesses, developers, communication providers and Tanzanian mobile-money infrastructure through one operating layer — routing, wallets, campaigns, delivery normalization and audit-ready billing.',
  primaryCta: { label: 'Start building', href: '/register' },
  secondaryCta: { label: 'Talk to sales', href: '/contact' },
  status: 'All channels operational',
  scrollHint: 'Scroll to explore the platform',
};

export const capabilities = [
  'Bulk SMS',
  'Sender IDs',
  'USSD menus',
  'IVR flows',
  'WhatsApp Business',
  'REST APIs',
  'Webhooks',
  'Wallet billing',
  'Campaign analytics',
  'Delivery reports',
  'RBAC & audit',
  'Multi-tenant orgs',
];

export const about = {
  headline: ['One platform,', 'six channels'],
  facts: [
    { label: 'HQ', value: 'Dar es Salaam, TZ' },
    { label: 'Billing', value: 'TZS wallet + invoices' },
    { label: 'Channels', value: 'SMS · USSD · Voice · IVR · WA · API' },
    { label: 'Audience', value: 'SMEs to enterprise' },
  ],
  paragraphs: [
    'MPANGE is not a thin wrapper around a single telecom provider. It is the operational layer customers depend on: routing, wallets, campaign orchestration, delivery normalization, signed callbacks, support and auditability.',
    'Organizations across Tanzania use MPANGE for promotions, OTP, fee reminders, citizen self-service, field coordination and fintech notifications — with cost visibility and traceability built in from day one.',
    'The interface, data model and operational flows reflect local business, institutional and payment realities — ready for multi-provider African expansion.',
  ],
};

export const stats = [
  { value: '6', label: 'Channels' },
  { value: '99.9%', label: 'Target uptime' },
  { value: 'TZS', label: 'Native billing' },
  { value: '24/7', label: 'Delivery trace' },
];

export const products = [
  {
    key: 'sms',
    title: 'SMS',
    href: '/products/sms',
    blurb: 'Bulk and transactional messaging with sender IDs, segment-aware cost control, delivery reports and campaign analytics.',
    items: ['Sender ID registration', 'Campaign workspaces', 'DLR normalization', 'Cost preview'],
    accent: '#00A3FF',
  },
  {
    key: 'ussd',
    title: 'USSD',
    href: '/products/ussd',
    blurb: 'Interactive mobile journeys for banking, education, NGOs, public service and field operations.',
    items: ['Menu builder', 'Session routing', 'Shortcode management', 'Usage analytics'],
    accent: '#4ade80',
  },
  {
    key: 'voice',
    title: 'Voice',
    href: '/products/voice',
    blurb: 'Outbound voice campaigns, TTS prompts, call logs and provider-abstracted voice routing.',
    items: ['Voice numbers', 'Call logs', 'Recordings', 'Route readiness'],
    accent: '#60a5fa',
  },
  {
    key: 'ivr',
    title: 'IVR Studio',
    href: '/products/ivr',
    blurb: 'No-code voice flows — menus, digit collection, branching, SMS follow-ups and call simulation.',
    items: ['Visual flow builder', 'DTMF menus', 'Call simulation', 'Publish & versions'],
    accent: '#38bdf8',
  },
  {
    key: 'whatsapp',
    title: 'WhatsApp',
    href: '/products/whatsapp',
    blurb: 'Unified business inbox, templates, broadcasts, media and operational customer profiles.',
    items: ['Template management', 'Shared inbox', 'Broadcasts', 'Media support'],
    accent: '#34d399',
  },
  {
    key: 'apis',
    title: 'Developer APIs',
    href: '/products/apis',
    blurb: 'API keys, signed webhooks, request logs, sandbox testing and SDK patterns.',
    items: ['Scoped API keys', 'Idempotency', 'Webhook retries', 'Request logs'],
    accent: '#a78bfa',
  },
  {
    key: 'wallet',
    title: 'Wallet + billing',
    href: '/pricing',
    blurb: 'Trusted top-ups, reservations, invoices, receipts, spend analytics and ledger visibility.',
    items: ['TZS wallet', 'Invoices & receipts', 'Spend analytics', 'Reservations'],
    accent: '#050A30',
  },
];

export const process = {
  phases: [
    {
      phase: '01',
      title: 'Connect',
      body: 'Create your organization, complete KYC, fund your wallet and configure team roles.',
      deliverables: ['Org workspace', 'Wallet top-up', 'RBAC setup'],
      duration: 'Day 1',
    },
    {
      phase: '02',
      title: 'Configure',
      body: 'Register sender IDs, set up channels, import contacts and connect your APIs or webhooks.',
      deliverables: ['Sender IDs', 'Channel config', 'API keys'],
      duration: 'Days 2–3',
    },
    {
      phase: '03',
      title: 'Launch',
      body: 'Run campaigns, send transactional traffic, monitor delivery and track spend in real time.',
      deliverables: ['Campaigns live', 'DLR tracking', 'Cost visibility'],
      duration: 'Week 1',
    },
    {
      phase: '04',
      title: 'Scale',
      body: 'Add automations, reseller customers, analytics dashboards and enterprise audit workflows.',
      deliverables: ['Analytics', 'Reseller mode', 'Audit exports'],
      duration: 'Ongoing',
    },
  ],
  stats: [
    { value: '< 5 min', label: 'First SMS send' },
    { value: '100%', label: 'Ledger traceability' },
    { value: 'Signed', label: 'Webhook events' },
  ],
};

export const faqs = [
  {
    q: 'What channels does MPANGE support?',
    a: 'SMS, USSD, Voice, IVR Studio, WhatsApp Business and REST APIs — all from one dashboard with unified billing and delivery tracking.',
  },
  {
    q: 'How does billing work?',
    a: 'Organizations fund a TZS wallet, with per-message pricing, cost previews before send, reservations for campaigns, invoices and full transaction history.',
  },
  {
    q: 'Can developers integrate via API?',
    a: 'Yes. Scoped API keys, idempotency keys, predictable error codes, request logs, signed webhooks and sandbox testing are available from the developer portal.',
  },
  {
    q: 'Is MPANGE built for Tanzanian operations?',
    a: 'Yes. TZS billing, local support hours, sender ID workflows and integrations aligned with Tanzanian mobile-money and telecom realities.',
  },
  {
    q: 'What about security and compliance?',
    a: 'KYC verification, RBAC, audit logs, encrypted provider secrets, signed webhooks and secure headers are built into the platform.',
  },
];

export const contact = {
  headline: ['Ready to run', 'communications at scale?'],
  body: 'Talk to our team about sender IDs, channel setup, API integration or enterprise deployment across your organization.',
  cta: 'Contact sales',
};

export const platform = {
  name: 'MPANGE Platform',
  role: 'Communication operating layer',
  shortBio: 'Unified routing, wallet billing, campaign orchestration and signed delivery callbacks for Tanzanian businesses.',
};

export const channelCategories = [
  {
    label: 'Messaging',
    icon: '◈',
    tools: [
      ['SMS', 'Bulk and transactional sends with sender IDs and DLR normalization'],
      ['WhatsApp', 'Templates, broadcasts and shared inbox workflows'],
      ['Campaigns', 'Import, personalize, schedule and track delivery'],
      ['Contacts', 'Groups, tags and segment-aware targeting'],
    ] as [string, string][],
  },
  {
    label: 'Interactive',
    icon: '◎',
    tools: [
      ['USSD', 'Menu-driven mobile journeys and session routing'],
      ['Voice', 'Outbound calls, TTS prompts and recordings'],
      ['IVR Studio', 'Visual call flows, menus, DTMF and simulation'],
      ['Shortcodes', 'Provisioning workflows and route readiness'],
    ] as [string, string][],
  },
  {
    label: 'Developer',
    icon: '⌘',
    tools: [
      ['REST API', 'Versioned endpoints with OpenAPI documentation'],
      ['Webhooks', 'Signed events, retries and delivery logs'],
      ['API keys', 'Scoped test and live secrets with RBAC'],
      ['SDKs', 'JavaScript, PHP and Python integration patterns'],
    ] as [string, string][],
  },
  {
    label: 'Operations',
    icon: '◆',
    tools: [
      ['Wallet', 'TZS prepaid billing with reservations and receipts'],
      ['Audit', 'Immutable logs for sends, billing and admin actions'],
      ['Support', 'Ticket workflows tied to delivery evidence'],
      ['Reseller', 'Subaccounts, margins and allocated credit'],
    ] as [string, string][],
  },
];

export type CaseStudy = {
  name: string;
  kind: string;
  year: string;
  summary: string;
  role: string;
  tags: string[];
  gradient: [string, string];
};

export const caseStudies: CaseStudy[] = [
  {
    name: 'Kilimanjaro Schools',
    kind: 'Education',
    year: '2025',
    summary: 'Fee reminders, parent broadcasts and admissions workflows across 12,000 contacts with wallet cost control.',
    role: 'SMS campaigns — operations team',
    tags: ['SMS', 'Campaigns', 'Wallet'],
    gradient: ['#1FB0FF', '#050A30'],
  },
  {
    name: 'Dar Logistics',
    kind: 'Logistics',
    year: '2025',
    summary: 'Dispatch updates and driver coordination via SMS with delivery reports linked to support tickets.',
    role: 'Transactional SMS — engineering',
    tags: ['SMS', 'DLR', 'Support'],
    gradient: ['#60a5fa', '#2563eb'],
  },
  {
    name: 'Mwanza Retail Group',
    kind: 'Retail',
    year: '2024',
    summary: 'Promotional bursts and loyalty updates for 40+ outlets with segment-aware pricing previews.',
    role: 'Bulk SMS — marketing ops',
    tags: ['SMS', 'Segments', 'Analytics'],
    gradient: ['#34d399', '#059669'],
  },
  {
    name: 'Citizen Self-Service',
    kind: 'Public sector',
    year: '2024',
    summary: 'USSD eligibility checks and citizen alerts with audit-ready session logs and provider normalization.',
    role: 'USSD + SMS — platform team',
    tags: ['USSD', 'Audit', 'Routing'],
    gradient: ['#a78bfa', '#7c3aed'],
  },
];

export const customerStories = [
  {
    type: 'Fintech',
    period: '2024 — Present',
    title: 'OTP and repayment notifications at scale',
    body: 'A Tanzanian fintech routes high-reliability OTP and wallet alerts through MPANGE with signed webhooks, idempotency keys and sub-second delivery tracing.',
    highlights: ['API integration', 'Signed webhooks', 'TZS billing'],
  },
  {
    type: 'NGO',
    period: '2024 — 2025',
    title: 'Field coordination across rural networks',
    body: 'An NGO uses USSD surveys and SMS beneficiary updates with offline-friendly reach, session analytics and auditable send history for donor reporting.',
    highlights: ['USSD menus', 'Bulk SMS', 'Audit exports'],
  },
  {
    type: 'Enterprise',
    period: '2023 — Present',
    title: 'Reseller program with allocated credit',
    body: 'A regional agency manages 40+ customer organizations with price books, wallet allocation, margin tracking and scoped subaccount visibility.',
    highlights: ['Reseller mode', 'Price books', 'Statements'],
  },
  {
    type: 'SME',
    period: '2025',
    title: 'Same-day campaign launch',
    body: 'A retail chain imported contacts, previewed encoding costs, reserved wallet funds and sent a nationwide promotion within one business day.',
    highlights: ['Campaign workspace', 'Cost preview', 'DLR reports'],
  },
];

export const developerHub = {
  profileUrl: '/developers',
  username: 'mpange',
  bio: 'APIs, SDKs and webhook tooling for African communication infrastructure.',
  languages: [
    { name: 'TypeScript', percent: 42 },
    { name: 'PHP', percent: 28 },
    { name: 'Python', percent: 18 },
    { name: 'Other', percent: 12 },
  ],
  repos: [
    {
      name: 'mpange-sdk-js',
      description: 'Official JavaScript SDK for SMS, webhooks and wallet APIs.',
      language: 'TypeScript',
      stars: 24,
      forks: 8,
      url: '/developers',
    },
    {
      name: 'mpange-webhooks',
      description: 'Webhook signature verification and retry handling examples.',
      language: 'TypeScript',
      stars: 11,
      forks: 4,
      url: '/developers',
    },
    {
      name: 'mpange-ussd-starter',
      description: 'USSD session handler patterns for Node and Laravel.',
      language: 'PHP',
      stars: 9,
      forks: 3,
      url: '/developers',
    },
  ],
};

export const docsBand = {
  headline: ['Ship on MPANGE', 'with confidence'],
  body: 'Explore developer docs, create an API key, fund your wallet and send your first message in minutes.',
};

export type DocNavItem = {
  id: string;
  label: string;
  summary: string;
  method: string;
  endpoint: string;
  requestLanguage: 'json' | 'bash' | 'typescript' | 'http';
  responseLanguage: 'json' | 'xml';
  request: string;
  response: string;
  statusLabel: string;
  responseMeta: string;
  latency?: string;
  status: string;
  pillars: { title: string; body: string }[];
};

export const docNavItems: DocNavItem[] = [
  {
    id: 'quickstart',
    label: 'Quickstart',
    summary: 'Send your first SMS with a test key, wallet reservation and delivery callback in under five minutes.',
    method: 'POST',
    endpoint: '/v1/sms/messages',
    requestLanguage: 'bash',
    responseLanguage: 'json',
    request: `curl -X POST https://api.mpange.co.tz/v1/sms/messages \\
  -H "Authorization: Bearer mp_test_4f8c2a91e6b0d3f7" \\
  -H "Content-Type: application/json" \\
  -H "Idempotency-Key: welcome-sms-001" \\
  -d '{
    "to": "+255683859574",
    "from": "MPANGE",
    "message": "Karibu! Your MPANGE account is ready."
  }'`,
    response: `{
  "object": "message",
  "id": "msg_01J9X4K2M8N5P7Q3R6T1V4W8Y0",
  "status": "queued",
  "to": "+255683859574",
  "from": "MPANGE",
  "segments": 1,
  "encoding": "GSM-7",
  "estimated_cost": {
    "amount": 32,
    "currency": "TZS"
  },
  "wallet_hold_id": "wh_01J9X4K2M8N5P7Q3R6T1V4W8Y1",
  "created_at": "2025-09-07T11:42:18.204Z"
}`,
    statusLabel: '201 Created',
    responseMeta: 'application/json · sandbox',
    latency: '38ms',
    status: 'queued',
    pillars: [
      { title: 'Test keys', body: 'Start in sandbox without touching live wallet balance.' },
      { title: 'Wallet hold', body: 'Cost is reserved before the message enters the route queue.' },
      { title: 'DLR callback', body: 'Delivery status posts back to your HTTPS endpoint.' },
    ],
  },
  {
    id: 'authentication',
    label: 'Authentication',
    summary: 'Authenticate every request with scoped Bearer keys and inspect the active organization context.',
    method: 'GET',
    endpoint: '/v1/account',
    requestLanguage: 'bash',
    responseLanguage: 'json',
    request: `curl https://api.mpange.co.tz/v1/account \\
  -H "Authorization: Bearer mp_live_7c2e9f41a8b6d0c3" \\
  -H "Accept: application/json"`,
    response: `{
  "object": "account",
  "organization_id": "org_01J9X4K2M8N5P7Q3R6T1V4W8Y2",
  "organization": "Acme Logistics Ltd",
  "environment": "live",
  "country": "TZ",
  "wallet": {
    "available": 1842500,
    "currency": "TZS"
  },
  "scopes": [
    "sms:send",
    "ussd:write",
    "wallet:read",
    "webhooks:manage"
  ]
}`,
    statusLabel: '200 OK',
    responseMeta: 'application/json · live',
    latency: '24ms',
    status: 'authenticated',
    pillars: [
      { title: 'Bearer auth', body: 'Pass mp_test_* or mp_live_* keys on every API call.' },
      { title: 'Scoped access', body: 'Keys can be limited to send, read, billing or webhook scopes.' },
      { title: 'Org context', body: 'Responses include organization, wallet and environment metadata.' },
    ],
  },
  {
    id: 'send-sms',
    label: 'Send SMS',
    summary: 'Queue outbound SMS with encoding preview, wallet reservation and signed delivery callbacks.',
    method: 'POST',
    endpoint: '/v1/sms/messages',
    requestLanguage: 'json',
    responseLanguage: 'json',
    request: `{
  "to": "+255683859574",
  "from": "MPANGE",
  "message": "Your verification code is 483920. Valid for 10 minutes.",
  "callback_url": "https://api.acme.co.tz/hooks/mpange/dlr",
  "idempotency_key": "otp-483920-683859574"
}`,
    response: `{
  "object": "message",
  "id": "msg_01J9X4K2M8N5P7Q3R6T1V4W8Y3",
  "status": "queued",
  "to": "+255683859574",
  "from": "MPANGE",
  "segments": 1,
  "encoding": "GSM-7",
  "estimated_cost": {
    "amount": 32,
    "currency": "TZS"
  },
  "route": {
    "provider": "vodacom_tz",
    "priority": "standard"
  },
  "created_at": "2025-09-07T11:43:02.881Z"
}`,
    statusLabel: '201 Created',
    responseMeta: 'application/json · queued',
    latency: '41ms',
    status: 'queued',
    pillars: [
      { title: 'Encoding preview', body: 'GSM-7 and Unicode segments are calculated before send.' },
      { title: 'Route selection', body: 'MPANGE picks the best provider route for the destination network.' },
      { title: 'DLR signing', body: 'Delivery callbacks include timestamped HMAC signatures.' },
    ],
  },
  {
    id: 'ussd-sessions',
    label: 'USSD Sessions',
    summary: 'Handle menu flows, session state and provider callbacks from one endpoint.',
    method: 'POST',
    endpoint: '/v1/ussd/sessions',
    requestLanguage: 'json',
    responseLanguage: 'json',
    request: `{
  "session_id": "ussd_01J9X4K2M8N5P7Q3R6T1V4W8Y0",
  "msisdn": "+255683859574",
  "input": "1",
  "service_code": "*149*01#",
  "network": "vodacom",
  "new_session": false
}`,
    response: `{
  "object": "ussd.session",
  "session_id": "ussd_01J9X4K2M8N5P7Q3R6T1V4W8Y0",
  "message": "Enter amount in TZS:",
  "continue_session": true,
  "menu_level": 2,
  "expires_at": "2025-09-07T11:47:18.204Z"
}`,
    statusLabel: '200 OK',
    responseMeta: 'application/json · session active',
    latency: '29ms',
    status: 'active',
    pillars: [
      { title: 'Session state', body: 'Menu level, expiry and subscriber input persist across requests.' },
      { title: 'Provider bridge', body: 'One endpoint normalizes Vodacom, Airtel and Tigo USSD payloads.' },
      { title: 'Wallet hooks', body: 'Billable USSD flows can reserve funds before menu completion.' },
    ],
  },
  {
    id: 'voice-calls',
    label: 'Voice / IVR',
    summary: 'Place outbound Africa\'s Talking calls and run inbound IVR Studio flows on answer via VoiceXML callbacks.',
    method: 'POST',
    endpoint: '/organizations/{orgId}/channels/voice/calls',
    requestLanguage: 'json',
    responseLanguage: 'json',
    request: `{
  "to": "+255712345678",
  "from": "+255700000001"
}`,
    response: `{
  "data": {
    "id": "…",
    "status": "submitted",
    "from": "+255700000001",
    "to": "+255712345678",
    "providerSessionId": "ATVId_…"
  },
  "message": "Call submitted to provider."
}`,
    statusLabel: '201 Created',
    responseMeta: 'application/json · submitted',
    latency: '52ms',
    status: 'submitted',
    pillars: [
      { title: 'Outbound dial', body: 'POST voice/calls queues AT /call using your AFRICASTALKING_* credentials.' },
      { title: 'Inbound IVR', body: 'Answered calls hit /callbacks/africas-talking/voice/calls?token=SECRET.' },
      { title: 'From number', body: 'Use an org voice number or AFRICASTALKING_VOICE_NUMBER.' },
    ],
  },
  {
    id: 'ivr-studio',
    label: 'IVR Studio',
    summary: 'Publish no-code IVR flows and route Africa\'s Talking voice callbacks into the MPANGE runtime.',
    method: 'POST',
    endpoint: '/callbacks/africas-talking/voice/calls',
    requestLanguage: 'json',
    responseLanguage: 'xml',
    request: `{
  "sessionId": "ATCid_xxx",
  "callerNumber": "+255712345678",
  "destinationNumber": "+255700000001",
  "dtmfDigits": "1"
}`,
    response: `<?xml version="1.0" encoding="UTF-8"?>
<Response>
  <GetDigits callbackUrl="https://api.mpange.example/api/callbacks/africas-talking/voice/calls?token=SECRET&exec=..." numDigits="1">
    <Say voice="woman">Chagua kitufe.</Say>
  </GetDigits>
</Response>`,
    statusLabel: '200 OK',
    responseMeta: 'application/xml · VoiceXML',
    latency: '38ms',
    status: 'active',
    pillars: [
      { title: 'Published flows', body: 'Assign a voice number in IVR Studio and publish a validated graph.' },
      { title: 'DTMF runtime', body: 'Menus, gather, speak, SMS and transfer nodes execute on each callback.' },
      { title: 'Callback URL', body: 'Point Africa\'s Talking to /api/callbacks/africas-talking/voice/calls?token=SECRET.' },
    ],
  },
  {
    id: 'whatsapp-templates',
    label: 'WhatsApp Templates',
    summary: 'Send Meta Cloud API template messages with per-account access tokens stored encrypted.',
    method: 'POST',
    endpoint: '/organizations/{orgId}/channels/whatsapp/messages',
    requestLanguage: 'json',
    responseLanguage: 'json',
    request: `{
  "whatsappAccountId": "…",
  "to": "+255683859574",
  "templateName": "order_update_v2",
  "language": "sw"
}`,
    response: `{
  "data": {
    "success": true,
    "to": "+255683859574",
    "templateName": "order_update_v2",
    "providerMessageId": "wamid.…",
    "status": "submitted"
  },
  "message": "WhatsApp template submitted."
}`,
    statusLabel: '200 OK',
    responseMeta: 'application/json · submitted',
    latency: '46ms',
    status: 'submitted',
    pillars: [
      { title: 'Cloud API', body: 'Uses graph.facebook.com/{version}/{phoneNumberId}/messages.' },
      { title: 'Encrypted tokens', body: 'Store a Meta access token when connecting the WhatsApp account.' },
      { title: 'Templates', body: 'Template must exist on the account (approved or pending inventory).' },
    ],
  },
  {
    id: 'webhooks',
    label: 'Webhooks',
    summary: 'Verify signed callbacks, replay failed events and inspect delivery history.',
    method: 'POST',
    endpoint: '/webhooks/mpange',
    requestLanguage: 'http',
    responseLanguage: 'json',
    request: `POST /webhooks/mpange HTTP/1.1
Host: api.acme.co.tz
Content-Type: application/json
X-MPANGE-Signature: t=1725706938,v1=8f4b2c91e0a7d6f5
X-MPANGE-Event-Id: evt_01J9X4K2M8N5P7Q3R6T1V4W8Y6

{
  "event": "message.delivered",
  "message_id": "msg_01J9X4K2M8N5P7Q3R6T1V4W8Y3",
  "to": "+255683859574",
  "status": "delivered",
  "delivered_at": "2025-09-07T11:43:09.118Z"
}`,
    response: `{
  "received": true,
  "verified": true,
  "event_id": "evt_01J9X4K2M8N5P7Q3R6T1V4W8Y6",
  "processed_at": "2025-09-07T11:43:09.204Z"
}`,
    statusLabel: '200 OK',
    responseMeta: 'application/json · verified',
    latency: '12ms',
    status: 'verified',
    pillars: [
      { title: 'HMAC signing', body: 'Every callback includes a timestamped signature for verification.' },
      { title: 'Retry policy', body: 'Failed endpoints are retried with exponential backoff.' },
      { title: 'Event log', body: 'Inspect payload, signature status and replay history in the dashboard.' },
    ],
  },
  {
    id: 'errors',
    label: 'Errors',
    summary: 'Structured error codes for validation, billing, routing and provider failures.',
    method: 'POST',
    endpoint: '/v1/sms/messages',
    requestLanguage: 'json',
    responseLanguage: 'json',
    request: `{
  "to": "0683859574",
  "from": "MPANGE",
  "message": "Payment reminder for invoice INV-2201"
}`,
    response: `{
  "error": {
    "type": "invalid_request_error",
    "code": "invalid_msisdn",
    "message": "Phone number must use E.164 format, for example +255683859574.",
    "param": "to",
    "request_id": "req_01J9X4K2M8N5P7Q3R6T1V4W8Y7"
  }
}`,
    statusLabel: '422 Unprocessable',
    responseMeta: 'application/json · validation error',
    latency: '18ms',
    status: '422',
    pillars: [
      { title: 'Typed errors', body: 'Each failure includes code, message and the offending field.' },
      { title: 'Request IDs', body: 'Support can trace any failed call with req_* identifiers.' },
      { title: 'Safe retries', body: 'Idempotency keys prevent duplicate sends after client retries.' },
    ],
  },
  {
    id: 'sdks',
    label: 'SDKs',
    summary: 'Use official SDKs with typed helpers for SMS, webhooks, wallet and session APIs.',
    method: 'SDK',
    endpoint: '@mpange/sdk',
    requestLanguage: 'typescript',
    responseLanguage: 'json',
    request: `import { Mpange } from '@mpange/sdk';

const mpange = new Mpange({
  apiKey: process.env.MPANGE_API_KEY!,
  environment: 'live',
});

const message = await mpange.sms.messages.create({
  to: '+255683859574',
  from: 'MPANGE',
  message: 'Payment received for order ORD-1042.',
  callbackUrl: 'https://api.acme.co.tz/hooks/mpange/dlr',
});`,
    response: `{
  "object": "message",
  "id": "msg_01J9X4K2M8N5P7Q3R6T1V4W8Y8",
  "status": "queued",
  "sdk": "@mpange/sdk@1.4.2",
  "request_id": "req_01J9X4K2M8N5P7Q3R6T1V4W8Y9"
}`,
    statusLabel: '201 Created',
    responseMeta: 'application/json · sdk response',
    latency: '44ms',
    status: 'installed',
    pillars: [
      { title: 'TypeScript first', body: 'Fully typed requests, responses and webhook event payloads.' },
      { title: 'Multi-runtime', body: 'JavaScript, PHP and Python clients share the same API surface.' },
      { title: 'Webhook helpers', body: 'Built-in signature verification and retry-safe parsing utilities.' },
    ],
  },
];
